import { useState, useEffect } from 'react'
import { FiX, FiLoader, FiSave, FiEye, FiEyeOff, FiExternalLink } from 'react-icons/fi'
import axios from 'axios'
import toast from 'react-hot-toast'

export default function TelegramSettingsModal({ isOpen, onClose, website, onSuccess }) {
  const [formData, setFormData] = useState({
    enabled: false,
    botToken: '',
    chatId: '',
    groupName: '',
    showBotToken: false,
    showChatId: false
  })
  const [saving, setSaving] = useState(false)
  const [testing, setTesting] = useState(false)
  const [currentConfig, setCurrentConfig] = useState(null)

  useEffect(() => {
    if (website && isOpen) {
      // Load current configuration status (masked values)
      fetchCurrentTelegramConfig()
      // Reset form to empty - don't pre-fill encrypted credentials
      setFormData({
        enabled: false,
        botToken: '',
        chatId: '',
        groupName: 'Order Notifications',
        showBotToken: false,
        showChatId: false
      })
    }
  }, [website, isOpen])

  const fetchCurrentTelegramConfig = async () => {
    try {
      const response = await axios.get(`/website-payment-config/telegram/${website._id}`)
      if (response.data.success) {
        setCurrentConfig(response.data.telegramConfig)
        // Only set enabled status, not the credentials
        setFormData(prev => ({
          ...prev,
          enabled: response.data.telegramConfig?.enabled || false
        }))
      }
    } catch (error) {
      console.log('Could not fetch current config (may not exist yet):', error.response?.data?.message)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
  }

  const handleToggleShowToken = (field) => {
    setFormData(prev => ({
      ...prev,
      [field]: !prev[field]
    }))
  }

  const handleTestTelegramConnection = async () => {
    if (!formData.botToken || !formData.chatId) {
      toast.error('Please enter both bot token and chat ID')
      return
    }

    setTesting(true)
    try {
      const response = await axios.post(`/website-payment-config/telegram/${website._id}/test`, {
        botToken: formData.botToken.trim(),
        chatId: formData.chatId.trim()
      })

      if (response.data.success) {
        toast.success(response.data.message || 'Telegram bot connection verified! ✅')
      } else {
        toast.error(response.data.message || 'Telegram configuration test failed')
      }
    } catch (error) {
      console.error('Error testing telegram config:', error)
      toast.error(error.response?.data?.message || 'Failed to test telegram configuration')
    } finally {
      setTesting(false)
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    // If disabling, just send the disable request
    if (!formData.enabled) {
      setSaving(true)
      axios.put(`/website-payment-config/telegram/${website._id}`, {
        enabled: false
      })
        .then(() => {
          toast.success('Telegram notifications disabled')
          setCurrentConfig(null)
          onSuccess()
        })
        .catch(error => {
          console.error('Error disabling telegram:', error)
          toast.error(error.response?.data?.message || 'Failed to disable telegram')
        })
        .finally(() => {
          setSaving(false)
        })
      return
    }

    // Validate when enabling
    if (!formData.botToken.trim()) {
      toast.error('Telegram bot token is required')
      return
    }
    if (!formData.chatId.trim()) {
      toast.error('Telegram chat ID is required')
      return
    }

    setSaving(true)
    const submitData = {
      enabled: true,
      botToken: formData.botToken.trim(),
      chatId: formData.chatId.trim(),
      groupName: formData.groupName?.trim() || 'Order Notifications'
    }

    axios.put(`/website-payment-config/telegram/${website._id}`, submitData)
      .then((response) => {
        toast.success(response.data.message || 'Telegram settings saved successfully! ✅')
        // Reset form and reload config
        fetchCurrentTelegramConfig()
        onSuccess()
      })
      .catch(error => {
        console.error('Error saving telegram settings:', error)
        toast.error(error.response?.data?.message || 'Failed to save telegram settings')
      })
      .finally(() => {
        setSaving(false)
      })
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-2xl font-bold text-gray-900">Telegram Settings</h2>
          <button
            onClick={onClose}
            disabled={saving}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            <FiX className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Current Status Banner */}
          {currentConfig?.enabled && (
            <div className="bg-green-50 border border-green-300 p-4 rounded-lg">
              <p className="text-sm font-semibold text-green-900">✅ Telegram is Currently Active</p>
              <p className="text-xs text-green-800 mt-2">
                Bot Token: <code className="bg-green-100 px-2 py-1 rounded">{currentConfig.botToken}</code>
              </p>
              <p className="text-xs text-green-800 mt-1">
                Chat ID: <code className="bg-green-100 px-2 py-1 rounded">{currentConfig.chatId}</code>
              </p>
              <p className="text-xs text-green-700 mt-2 font-semibold">
                To update: Enter new credentials below and test before saving.
              </p>
            </div>
          )}

          {/* Security Banner */}
          <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg space-y-3">
            <p className="text-sm font-semibold text-blue-900">🔒 Security & Encryption</p>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
              <li>All Telegram credentials are encrypted at rest using AES-256-GCM</li>
              <li>Credentials are never exposed in logs or API responses</li>
              <li>Only the backend uses credentials to send notifications</li>
              <li>Encryption keys are stored as environment variables</li>
              <li>Frontend never receives or stores sensitive telegram credentials</li>
            </ul>
          </div>

          {/* Enable Telegram */}
          <div className="space-y-4">
            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                name="enabled"
                checked={formData.enabled}
                onChange={handleInputChange}
                className="w-5 h-5 rounded border-gray-300 cursor-pointer"
              />
              <span className="text-lg font-semibold text-gray-900">Enable Telegram Notifications</span>
            </label>
            <p className="text-sm text-gray-600 ml-8">
              Send notifications to Telegram when customers submit contact forms or complete payments. Once enabled, all website categories will automatically send notifications to your configured Telegram chat.
            </p>
          </div>

          {/* Telegram Settings (shown if enabled) */}
          {formData.enabled && (
            <div className="space-y-4 pt-4 border-t border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Telegram Bot Configuration</h3>

              {/* Warning Box */}
              <div className="bg-red-50 border border-red-200 p-4 rounded-lg">
                <p className="text-sm font-semibold text-red-900 mb-2">⚠️ Important Security Notice</p>
                <ul className="text-xs text-red-800 space-y-1 list-disc list-inside">
                  <li>Never share your bot token with anyone</li>
                  <li>Do not commit credentials to version control</li>
                  <li>If token is compromised, regenerate it immediately</li>
                  <li>Tokens are encrypted before storage</li>
                  <li>Keep chat ID confidential</li>
                </ul>
              </div>

              {/* Bot Token */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telegram Bot Token *
                </label>
                <div className="relative">
                  <input
                    type={formData.showBotToken ? 'text' : 'password'}
                    name="botToken"
                    value={formData.botToken}
                    onChange={handleInputChange}
                    placeholder="123456:ABC-DEF1294Y58Si2E_kc4Yh5r-U1234ew11"
                    className="input-field w-full pr-10"
                    required={formData.enabled}
                  />
                  <button
                    type="button"
                    onClick={() => handleToggleShowToken('showBotToken')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {formData.showBotToken ? (
                      <FiEyeOff className="w-4 h-4" />
                    ) : (
                      <FiEye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Keep your bot token secure, never share it publicly
                </p>
              </div>

              {/* Chat ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Telegram Chat ID *
                </label>
                <div className="relative">
                  <input
                    type={formData.showChatId ? 'text' : 'password'}
                    name="chatId"
                    value={formData.chatId}
                    onChange={handleInputChange}
                    placeholder="-1001234567890 or 987654321"
                    className="input-field w-full pr-10"
                    required={formData.enabled}
                  />
                  <button
                    type="button"
                    onClick={() => handleToggleShowToken('showChatId')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {formData.showChatId ? (
                      <FiEyeOff className="w-4 h-4" />
                    ) : (
                      <FiEye className="w-4 h-4" />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Group chat IDs start with -, personal chat IDs are numeric
                </p>
              </div>

              {/* Group Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Group/Channel Name
                </label>
                <input
                  type="text"
                  name="groupName"
                  value={formData.groupName}
                  onChange={handleInputChange}
                  placeholder="Order Notifications"
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Display name for notification messages (e.g., "Order Notifications", "Customer Inquiries")
                </p>
              </div>

              {/* Test Connection */}
              <button
                type="button"
                onClick={handleTestTelegramConnection}
                disabled={testing || !formData.botToken || !formData.chatId}
                className="w-full py-2 px-4 bg-blue-50 text-blue-600 hover:bg-blue-100 disabled:bg-gray-100 disabled:text-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
              >
                {testing ? (
                  <>
                    <FiLoader className="w-4 h-4 animate-spin" /> Testing...
                  </>
                ) : (
                  'Test Telegram Connection'
                )}
              </button>

              {/* Info Box */}
              <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-lg space-y-2">
                <p className="text-sm font-semibold text-yellow-900">ℹ️ How to Get Your Credentials</p>
                <ol className="text-xs text-yellow-800 space-y-2 list-decimal list-inside">
                  <li>
                    <strong>Create a Bot:</strong> Talk to{' '}
                    <a href="https://t.me/botfather" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                      @BotFather on Telegram <FiExternalLink className="inline w-3 h-3" />
                    </a>
                  </li>
                  <li>
                    <strong>Get Bot Token:</strong> Use /newbot command, copy the token provided
                  </li>
                  <li>
                    <strong>Get Chat ID:</strong> Send a message in your group/chat, then use{' '}
                    <a href="https://t.me/userinfobot" target="_blank" rel="noopener noreferrer" className="underline font-semibold">
                      @userinfobot <FiExternalLink className="inline w-3 h-3" />
                    </a>{' '}
                    to get the chat ID
                  </li>
                  <li>Add your bot to the group/chat</li>
                  <li>Paste the credentials here and test</li>
                </ol>
              </div>

              {/* Auto-Apply Info */}
              <div className="bg-green-50 border border-green-200 p-4 rounded-lg space-y-2">
                <p className="text-sm font-semibold text-green-900">✓ Automatic Notification Setup</p>
                <p className="text-xs text-green-800 space-y-1">
                  <div>Once configured and saved, Telegram notifications will automatically:</div>
                  <div className="ml-4 mt-1 space-y-1">
                    <div>• Send messages when customers submit <strong>contact forms</strong></div>
                    <div>• Send alerts when <strong>payment succeeds</strong> on any product/service</div>
                    <div>• Apply to <strong>all website categories</strong> (e-commerce, booking, contact, etc.)</div>
                  </div>
                  <div className="mt-2">No per-category setup needed—all notifications use this single configuration.</div>
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 py-2 px-4 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg font-medium transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || (formData.enabled && (!formData.botToken || !formData.chatId))}
              className="flex-1 py-2 px-4 bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-400 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 disabled:cursor-not-allowed"
            >
              {saving ? (
                <>
                  <FiLoader className="w-4 h-4 animate-spin" /> Saving...
                </>
              ) : (
                <>
                  <FiSave className="w-4 h-4" /> Save Settings
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
